import React, { useEffect, useRef, useState } from 'react';
import { Bot, Send, Wrench, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { waApi, errMsg } from './waApi';

const Bubble = ({ m }) => {
  const mine = m.direction === 'in';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`wa-bubble-${m.direction}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed shadow ${
        mine ? 'bg-[#005c4b] text-emerald-50 rounded-br-sm' : 'bg-zinc-800 text-zinc-100 rounded-bl-sm'}`}>
        {m.text}
        {!mine && m.meta?.used_tools?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {m.meta.used_tools.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[10px] text-zinc-400 bg-zinc-900 border border-zinc-700 rounded-full px-1.5 py-0.5"><Wrench size={9} />{t.name}</span>
            ))}
          </div>
        )}
        {!mine && m.meta?.awaiting_confirmation && (
          <div className="mt-1.5 text-[10px] text-amber-300 flex items-center gap-1"><ShieldCheck size={10} /> waiting for YES / NO</div>
        )}
        <div className="text-[10px] text-zinc-400/70 text-right mt-1">{new Date(m.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
    </div>
  );
};

const SUGGESTIONS = ['hi', 'who owes us money?', 'stock of maltodextrin', 'can we make 10 batches of cheese & herbs?', 'production for Monday 5 batches of Schezwan', 'create order for Anil Traders 200 kg Peri Peri'];

export const WaConsole = ({ users }) => {
  const linked = users.filter(u => u.whatsapp.enabled);
  const [userId, setUserId] = useState(linked[0]?.id || users[0]?.id || '');
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const user = users.find(u => u.id === userId);

  const load = async (id) => { if (!id) return; try { const r = await waApi.messages(id, 60); setMsgs(r.messages); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { load(userId); }, [userId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const send = async (t) => {
    const body = (t ?? text).trim();
    if (!body || !userId) return;
    setBusy(true); setText('');
    setMsgs(m => [...m, { direction: 'in', text: body, ts: new Date().toISOString(), id: 'tmp' }]);
    try { await waApi.simulate(userId, body); await load(userId); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="wa-console">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-zinc-950/60">
        <div className="w-9 h-9 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center"><Bot size={16} className="text-[#25D366]" /></div>
        <div>
          <div className="text-sm font-semibold text-zinc-100">TI · Bot Console</div>
          <div className="text-[11px] text-zinc-500">Chat as a user — exactly what they'd get on WhatsApp. Nothing is delivered from here.</div>
        </div>
        <select value={userId} onChange={e => setUserId(e.target.value)} data-testid="wa-console-user-select"
          className="ml-auto bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-1.5 text-xs text-zinc-100">
          {users.map(u => <option key={u.id} value={u.id}>{u.name} · {u.role}{u.whatsapp.enabled ? '' : ' (bot off)'}</option>)}
        </select>
      </div>
      <div className="h-[460px] overflow-y-auto px-4 py-4 space-y-2.5" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '14px 14px' }} data-testid="wa-console-thread">
        {msgs.length === 0 && <div className="text-center text-xs text-zinc-500 mt-24">No conversation yet with {user?.name}. Say <b>hi</b>.</div>}
        {msgs.map(m => <Bubble key={m.id} m={m} />)}
        {busy && <div className="text-xs text-zinc-500 italic">TI is typing…</div>}
        <div ref={endRef} />
      </div>
      <div className="px-4 py-2 flex gap-1.5 flex-wrap border-t border-zinc-800">
        {SUGGESTIONS.map(s => (
          <button key={s} onClick={() => send(s)} disabled={busy} data-testid="wa-console-suggestion"
            className="text-[11px] px-2.5 py-1 rounded-full border border-zinc-800 text-zinc-400 hover:text-emerald-200 hover:border-emerald-500/40 disabled:opacity-40">{s}</button>
        ))}
      </div>
      <form onSubmit={e => { e.preventDefault(); send(); }} className="flex gap-2 px-4 py-3 bg-zinc-950/60">
        <input value={text} onChange={e => setText(e.target.value)} placeholder={`Message TI as ${user?.name || 'user'}…`} data-testid="wa-console-input"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-full px-4 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/60" />
        <button type="submit" disabled={busy || !text.trim()} data-testid="wa-console-send"
          className="w-10 h-10 rounded-full bg-[#25D366] text-black flex items-center justify-center hover:bg-[#1ebe5a] disabled:opacity-40"><Send size={15} /></button>
      </form>
    </div>
  );
};
